<template>
  <v-card
    class="sucursal-card"
    :class="{ 'sucursal-cercana': esMasCercana }"
    flat
    :ripple="false"
  >
    <!-- Imagen del logo del cliente -->
    <v-img
      v-if="logoCliente"
      :src="logoCliente"
      :lazy-src="logoCliente"
      height="120"
      class="sucursal-logo primary"
      contain
      position="center"
    >
      <template v-slot:placeholder>
        <v-row
          class="fill-height ma-0"
          align="center"
          justify="center"
        >
          <v-progress-circular
            indeterminate
            color="grey lighten-5"
          ></v-progress-circular>
        </v-row>
      </template>
    </v-img>

    <v-card-text class="card-content">
      <div class="card-header">
        <h3 class="sucursal-nombre">{{ sucursal.Name || sucursal.Nombre }}</h3>
        <v-chip
          v-if="esMasCercana && sucursal.Distancia && sucursal.Distancia !== -1"
          small
          color="primary"
          class="chip-cercana"
        >
          <v-icon small left>mdi-map-marker-distance</v-icon>
          MÁS CERCANA
        </v-chip>
      </div>

      <!-- Estado cerrado -->
      <div
        v-if="sucursal.Cerrado"
        class="tienda-status tienda-status--warn mb-3"
      >
        <v-icon x-small class="mr-1">mdi-lock-outline</v-icon>
        Tienda cerrada
      </div>
      <div
        v-else-if="!tiendaAbierta"
        class="tienda-status tienda-status--info mb-3"
      >
        <v-icon x-small class="mr-1">mdi-clock-alert-outline</v-icon>
        {{ textoTiendaCerrada }}
      </div>

      <div class="sucursal-info">
        <div v-if="direccionCompleta" class="info-item">
          <v-icon small color="primary" class="info-icon">mdi-map-marker</v-icon>
          <span class="info-text">{{ direccionCompleta }}</span>
        </div>

        <div v-if="telefonos" class="info-item">
          <v-icon small color="primary" class="info-icon">mdi-phone</v-icon>
          <span class="info-text">{{ telefonos }}</span>
        </div>

        <div v-if="sucursal.Whatsapp" class="info-item whatsapp-item" @click="enviarWhatsapp">
          <v-icon small color="success" class="info-icon">mdi-whatsapp</v-icon>
          <span class="info-text whatsapp-text">{{ formatearWhatsapp(sucursal.Whatsapp) }}</span>
        </div>

        <div v-if="horarioActual" class="info-item">
          <v-icon small color="primary" class="info-icon">mdi-clock-time-three-outline</v-icon>
          <span class="info-text">{{ horarioActual }}</span>
        </div>

        <div v-if="mostrarDistancia" class="info-item">
          <v-icon small color="primary" class="info-icon">mdi-map-marker-distance</v-icon>
          <span class="info-text">{{ formatearDistancia(sucursal.Distancia) }}</span>
        </div>
      </div>
    </v-card-text>

    <v-card-actions class="card-actions">
      <v-btn
        v-if="sucursal.LinkWebVentas"
        block
        color="primary"
        large
        depressed
        @click="hacerPedido"
        class="btn-pedido"
      >
        <v-icon left>mdi-shopping</v-icon>
        Quiero hacer un pedido
      </v-btn>

      <v-btn
        v-else-if="sucursal.Code"
        block
        color="primary"
        large
        outlined
        @click="verTienda"
        class="btn-pedido"
      >
        <v-icon left>mdi-store</v-icon>
        Pedir aquí
      </v-btn>

      <v-btn
        v-else
        block
        color="primary"
        large
        outlined
        disabled
        class="btn-pedido"
      >
        No disponible
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import isOpen from '@/helpers/isOpen.js'
import schedules from '@/helpers/schedules.js'
import getNextDate from '@/helpers/getNextDate.js'

export default {
  name: 'SucursalCard',
  props: {
    sucursal: {
      type: Object,
      required: true
    },
    esMasCercana: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    logoCliente () {
      // Buscar el logo en diferentes campos posibles
      return this.sucursal.Img ||
             this.sucursal.Logo ||
             this.sucursal.Imagen ||
             this.sucursal.logo ||
             this.sucursal.img ||
             null
    },
    direccionCompleta () {
      const { Direccion, Ciudad, Provincia, Address } = this.sucursal

      // Si tiene Address (formato anterior), usarlo directamente
      if (Address) return Address

      // Si tiene Direccion, construir dirección completa
      if (Direccion) {
        const dir = Direccion.replace(', Argentina', '').trim()
        const partes = []

        if (dir) partes.push(dir)
        if (Ciudad && Ciudad !== dir) partes.push(Ciudad)
        if (Provincia && Provincia !== Ciudad) partes.push(Provincia)

        return partes.length > 0 ? partes.join(', ') : null
      }

      return null
    },
    telefonos () {
      const { Telefono, TelefonoAlternativo } = this.sucursal
      if (!Telefono) return null

      if (TelefonoAlternativo) {
        return `${Telefono} / ${TelefonoAlternativo}`
      }
      return Telefono
    },
    mostrarDistancia () {
      // La distancia es válida si es un número mayor a 0 y no es -1
      if (!this.sucursal.Distancia) return false
      if (this.sucursal.Distancia === -1) return false
      if (this.sucursal.Distancia === '0 Km' || this.sucursal.Distancia === '0') return false
      if (typeof this.sucursal.Distancia === 'string' && this.sucursal.Distancia.trim() === '') return false

      const numDistancia = typeof this.sucursal.Distancia === 'number'
        ? this.sucursal.Distancia
        : parseFloat(this.sucursal.Distancia)

      if (isNaN(numDistancia)) return false
      if (numDistancia <= 0) return false

      return true
    },
    horarios () {
      return this.sucursal.horarios || this.sucursal.Horarios || []
    },
    horarioActual () {
      if (!this.horarios || this.horarios.length === 0) return null
      return schedules(this.horarios)
    },
    tiendaAbierta () {
      if (!this.horarios || this.horarios.length === 0) return true
      return isOpen(this.horarios)
    },
    textoTiendaCerrada () {
      if (this.tiendaAbierta) return ''
      if (!this.horarios || this.horarios.length === 0) return 'TIENDA CERRADA.'
      const resp = getNextDate(this.horarios)
      return resp === '' ? 'TIENDA CERRADA.' : `Cerrado. ${resp}`
    }
  },
  methods: {
    hacerPedido () {
      if (this.sucursal.LinkWebVentas) {
        window.open(this.sucursal.LinkWebVentas, '_blank')
      }
    },
    verTienda () {
      if (this.sucursal.Code) {
        // Limpiar cache antes de navegar
        if (this.$store) {
          this.$store.commit('Productos/setProducts', [])
          this.$store.commit('Categorias/setCategoria', null)
        }
        this.$router.push({ name: 'Home', params: { client: this.sucursal.Code } })
      }
    },
    enviarWhatsapp () {
      if (this.sucursal.Whatsapp) {
        const numero = this.sucursal.Whatsapp.toString().replace(/\D/g, '')
        const url = `https://api.whatsapp.com/send?phone=549${numero}`
        window.open(url, '_blank')
      }
    },
    formatearWhatsapp (numero) {
      if (!numero) return ''
      const num = numero.toString().replace(/\D/g, '')
      if (num.length === 10) {
        return `+54 9 ${num.slice(0, 3)} ${num.slice(3, 6)}-${num.slice(6)}`
      }
      return numero
    },
    formatearDistancia (distancia) {
      if (!distancia || distancia === -1) return null
      if (typeof distancia === 'string') return distancia
      if (distancia < 1000) {
        return `${Math.floor(distancia)} mts.`
      }
      return `${(distancia / 1000).toFixed(2)} km`
    }
  }
}
</script>

<style scoped>
.sucursal-card {
  margin-bottom: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px !important;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.sucursal-logo {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.sucursal-card:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
}

.sucursal-card.sucursal-cercana {
  border: 1.5px solid var(--v-primary-base, #7800C6);
  box-shadow: 0 2px 10px rgba(120, 0, 198, 0.12);
}

.card-content {
  padding: 16px 16px 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
}

.sucursal-nombre {
  font-size: 1.15rem;
  font-weight: 800;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
  flex: 1;
  line-height: 1.25;
}

.chip-cercana {
  margin-top: 2px;
  font-weight: 700 !important;
}

.sucursal-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.info-icon {
  margin-top: 2px;
  flex-shrink: 0;
}

.info-text {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.9rem;
  line-height: 1.4;
  word-break: break-word;
}

.whatsapp-item {
  cursor: pointer;
  padding: 4px;
  margin: -4px;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.whatsapp-item:hover {
  background-color: rgba(76, 175, 80, 0.1);
}

.whatsapp-text {
  color: #4CAF50;
  font-weight: 600;
}

.tienda-status {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.3;
}
.tienda-status--warn {
  background: #fff8e1;
  color: #8a6d00;
}
.tienda-status--info {
  background: #f5f5f5;
  color: rgba(0, 0, 0, 0.65);
}

.card-actions {
  padding: 8px 16px 16px;
}

.btn-pedido {
  height: 44px !important;
  border-radius: 12px !important;
  font-weight: 700 !important;
  letter-spacing: 0.02em !important;
  text-transform: none !important;
}

@media (max-width: 960px) {
  .sucursal-card {
    margin-bottom: 12px;
  }

  .sucursal-nombre {
    font-size: 1.05rem;
  }
}
</style>
